import { useId } from 'react'
import { PageSection } from '@/features/jobs/details/components/PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import {
  Card,
  CardContent,
  CardContentDecoration,
  CardContentFooterActions,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { dateFormatters } from '@/utils/date-util'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'

export interface FinalReturnDownloadSectionProps {
  onDownloadPress?: () => unknown
  uploadedAt: Date
  testId?: string
  dateTestId?: string
  downloadTestId?: string
}

/**
 * Section with a card for downloading the final return.
 */
export function FinalReturnDownloadSection({
  onDownloadPress,
  uploadedAt,
  testId,
  dateTestId,
  downloadTestId,
}: FinalReturnDownloadSectionProps) {
  const headlineId = useId()

  return (
    <PageSection>
      <SectionHeadline id={headlineId}>Your filed return</SectionHeadline>
      <Card testId={testId}>
        <CardContent>
          <CardContentDecoration>
            <Icon name="file" variant="surface-background" />
          </CardContentDecoration>
          <CardTitle>Download your tax return</CardTitle>
          <CardSubtitle testId={dateTestId}>
            Uploaded {dateFormatters.dateFull.format(uploadedAt)}
          </CardSubtitle>
          <CardContentFooterActions>
            <Button
              variant="text-inline"
              label="Download return"
              onPress={onDownloadPress}
              testId={downloadTestId}
            />
          </CardContentFooterActions>
        </CardContent>
      </Card>
    </PageSection>
  )
}

interface FinalReturnDownloadActionCardProps {
  onDownloadPress?: () => unknown
  testId?: string
  downloadTestId?: string
}

/**
 * Final Return Download Action Card
 */
export function FinalReturnDownloadActionCard({
  onDownloadPress,
  testId,
  downloadTestId,
}: FinalReturnDownloadActionCardProps) {
  return (
    <ActionCard
      kicker="Your tax return"
      title="Available for download"
      testId={testId}
    >
      <Button
        onPress={onDownloadPress}
        variant="outline"
        label="Download return"
        testId={downloadTestId}
      />
    </ActionCard>
  )
}

interface FinalReturnDownloadScrolledActionProps {
  onDownloadPress?: () => unknown
}

/**
 * Final Return Download Scrolled Action
 */
export function FinalReturnDownloadScrolledAction({
  onDownloadPress,
}: FinalReturnDownloadScrolledActionProps) {
  return (
    <ScrolledAction label="Download your return" onPress={onDownloadPress} />
  )
}
